/**
 * Arabic (ar) — Full RTL locale
 * Font: Noto Kufi Arabic (loaded via Google Fonts)
 * Direction: rtl — layout mirrors completely
 */
import type { Locale } from './en';

const ar: Locale = {
  meta: {
    appName:  'متيرنلي',
    tagline:  'نُؤثّر في الحياة، أمًّا تلو أمّ',
    language: 'العربية',
    dir:      'rtl',
    locale:   'ar-SA',
  },

  common: {
    install: { iosHint: "أضيفي متيرنلي إلى الشاشة الرئيسية: اضغطي على أيقونة المشاركة ثم «إضافة إلى الشاشة الرئيسية»." },
    continue:    'متابعة',
    back:        'رجوع',
    save:        'حفظ',
    cancel:      'إلغاء',
    close:       'إغلاق',
    loading:     'جارٍ التحميل…',
    error:       'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
    required:    'مطلوب',
    optional:    'اختياري',
    seeAll:      'عرض الكل',
    viewAll:     'عرض الكل',
    submit:      'إرسال',
    send:        'إرسال',
    next:        'التالي',
    skip:        'تخطّي في الوقت الحالي',
    done:        'تم',
    confirm:     'تأكيد',
    delete:      'حذف',
    edit:        'تعديل',
    search:      'بحث',
    noResults:   'لا توجد نتائج',
    minutes:     '{{n}} دقيقة',
    today:       'اليوم',
    yesterday:   'أمس',
  },

  nav: {
    home:      'الرئيسية',
    mood:      'المزاج',
    companion: 'المرافق',
    learn:     'التعلّم',
    profile:   'الملف',
  },

  verifyEmail: {
    working:       'جارٍ تأكيد بريدكِ الإلكتروني…',
    oneMoment:     'لحظة من فضلكِ.',
    doneTitle:     'تم تأكيد بريدكِ الإلكتروني',
    doneBody:      'يمكنكِ تسجيل الدخول الآن.',
    signIn:        'تسجيل الدخول',
    failedTitle:   'تعذّر تأكيد هذا الرابط',
    missingToken:  'هذا الرابط ينقصه رمز التحقق.',
    failed:        'هذا الرابط غير صالح أو انتهت صلاحيته.',
    failedHelp:    'تنتهي صلاحية روابط التحقق. سجّلي الدخول وسنرسل رابطًا جديدًا.',
    backToSignIn:  'العودة إلى تسجيل الدخول',
  },
  severity: {
    none: 'لا يوجد', minimal: 'طفيف جدًا', mild: 'طفيف', moderate: 'متوسط',
    moderately_severe: 'متوسط إلى شديد', severe: 'شديد', crisis: 'حرج',
  },
  auth: {
    restoreAccount: {
      prompt: "هذا الحساب مُجدوَل للحذف. سجّلي الدخول مجددًا لاستعادته.",
      action: "استعادة حسابي",
      working: "جارٍ الاستعادة…",
      failed: "تعذّرت استعادة هذا الحساب.",
    },
    resendVerification: "أرسلي لي رابط تحقق جديدًا",
    sending: "جارٍ الإرسال…",
    verificationResent: "إذا كان هذا الحساب بحاجة إلى تحقق، فقد أُرسل رابط جديد.",
    signIn:             'تسجيل الدخول',
    signOut:            'تسجيل الخروج',
    register:           'إنشاء حساب',
    email:              'البريد الإلكتروني',
    password:           'كلمة المرور',
    confirmPassword:    'تأكيد كلمة المرور',
    forgotPassword:     'نسيت كلمة المرور؟',
    resetPassword:      'إعادة تعيين كلمة المرور',
    welcomeBack:        'مرحبًا بعودتك',
    signInSubtitle:     'سجّلي دخولك لمواصلة رحلتك',
    newToApp:           'جديدة على متيرنلي؟',
    alreadyHaveAccount: 'لديكِ حساب بالفعل؟',
    createAccount:      'أنشئي حسابًا',
    checkEmail:         'تفقّدي بريدك الإلكتروني',
    checkEmailBody:     'أرسلنا رابط التحقق إلى {{email}}. افتحيه لتفعيل حسابك.',
    backToSignIn:       'العودة إلى تسجيل الدخول',
    passwordRules: {
      length:    '10 أحرف على الأقل',
      uppercase: 'حرف كبير واحد',
      lowercase: 'حرف صغير واحد',
      number:    'رقم واحد',
      special:   'رمز خاص واحد',
    },
    privacyNote: 'بالتسجيل، توافقين على شروط الخدمة وسياسة الخصوصية. بياناتكِ الصحية مشفّرة بـ AES-256 ولا يراها سوى فريق رعايتكِ.',
    immediateSupport: 'هل تحتاجين مساعدة فورية؟ ←',
  },

  onboarding: {
    dontAskAgain: 'لا تسأليني مجددًا',
    welcome:      'مرحبًا بكِ في متيرنلي',
    whereAreYou:  'أين أنتِ في رحلتكِ الآن؟',
    stages: {
      trying:     { label: 'أحاول الحمل',      sub: 'في رحلة الأمومة' },
      pregnant:   { label: 'حامل',             sub: 'في مرحلة الانتظار حاليًا' },
      postpartum: { label: 'ما بعد الولادة',   sub: 'بعد الولادة (حتى سنتين)' },
      loss:       { label: 'فقدان الحمل',      sub: 'إجهاض أو ولادة متأخرة أو فقدان رضيع' },
    },
    howFarAlong:   'كم أسبوعًا مضى على حملكِ؟',
    whenDidYouGive:'متى ولدتِ؟',
    takYourTime:   'خذي وقتكِ',
    lossMessage:   'نتألم لفقدانكِ. شاركي فقط ما تشعرين بالراحة في مشاركته.',
    gestWeeks:     'عمر الحمل (بالأسابيع)',
    approximate:   'تقريبي مقبول',
    birthDate:     'تاريخ الولادة (تقريبي)',
    whatCallYou:   'كيف نناديكِ؟',
    nameOptional:  'اختياري تمامًا — يمكننا استخدام بريدك الإلكتروني بدلاً من ذلك.',
    firstName:     'الاسم الأول',
    nameEncrypted: '🔒 اسمكِ مشفّر ولا يراه سوى أنتِ وفريق رعايتكِ.',
    fewThings:     'بعض الأمور السريعة',
    consent: {
      clinical: { label: 'مشاركة البيانات السريرية',  desc: 'أفهم أن نتائج تقييماتي وبيانات مزاجي ستُشارَك مع فريق الرعاية المعيّن لي لتقديم الدعم.' },
      push:     { label: 'تذكيرات العافية',            desc: 'أرسلي لي تذكيرات لطيفة لإكمال متابعاتي اليومية والتقييمات. (يمكنكِ إيقافها في أي وقت.)' },
      research: { label: 'المساهمة في البحث العلمي',  desc: 'السماح باستخدام بيانات مجهولة المصدر لتحسين نتائج صحة الأمومة. لا تتضمن بياناتكِ الشخصية أبدًا.' },
    },
    letsStart:   'أنا مستعدة — لنبدأ 🌱',
    privacyNote: 'بالمتابعة، توافقين على شروط الخدمة وسياسة الخصوصية. بياناتكِ محمية ومشفّرة.',
  },

  dashboard: {
    greeting: {
      morning:   'صباح الخير',
      afternoon: 'مساء الخير',
      evening:   'مساء الخير',
    },
    stages: {
      trying:     'أحاول الحمل',
      pregnant:   'حامل',
      postpartum: 'ما بعد الولادة',
      loss:       'فقدان الحمل',
    },
    riskLevels: {
      low:      'خطر منخفض',
      moderate: 'خطر متوسط',
      high:     'خطر مرتفع',
      crisis:   '⚠ دعم الأزمات نشط',
    },
    crisis: {
      supportTitle: "يبدو أن الأمور صعبة الآن. الدعم موجود متى ما أردتِه.",
      title:   'أنتِ لستِ وحدكِ. الدعم الفوري متاح.',
      more:    'المزيد من الموارد',
    },
    quickActions: {
      logMood:   'سجّلي مزاجكِ',
      talkItOut: 'تحدّثي عنه',
      checkIn:   'الفحص الدوري',
      resources: 'الموارد',
    },
    yourWeek:       'أسبوعكِ',
    recentCheckIns: 'الفحوصات الأخيرة',
    carePlan:       'خطة رعايتكِ',
    carePlanSub:    'اطّلعي على أهدافكِ ومهام العافية اليومية',
    chartLegend: {
      mood:    'المزاج',
      anxiety: 'القلق',
    },
  },

  mood: {
    title:        'كيف حالكِ اليوم؟',
    subtitle:     'يستغرق هذا نحو 60 ثانية ويساعدنا على دعمكِ بشكل أفضل.',
    overallMood:  'المزاج العام',
    scale: {
      veryLow: 'منخفض جدًا',
      low:     'منخفض',
      okay:    'مقبول',
      good:    'جيد',
      great:   'ممتاز',
    },
    energy:       'مستوى الطاقة',
    anxiety:      'مستوى القلق',
    sleep:        'النوم الليلة الماضية',
    energyLow:    'منهكة',
    energyHigh:   'نشيطة',
    anxietyLow:   'هادئة',
    anxietyHigh:  'قلقة جدًا',
    sleepLow:     '0 ساعة',
    sleepHigh:    '12 ساعة',
    tags:         'كيف تصفين حالتكِ؟',
    note:         'هل هناك شيء آخر في ذهنكِ؟',
    notePlaceholder: 'اكتبي بحرية — هذا خاص بكِ ومشفّر…',
    save:         'احفظي تسجيل اليوم',
    saved:        'تم التسجيل!',
    savedSub:     'تم حفظ تسجيل اليوم.',
    logAnother:   'تسجيل إدخال آخر',
    moodTags: [
      'قلقة', 'متعبة', 'مرهقة', 'متفائلة',
      'ممتنة', 'باكية', 'منفصلة', 'أرتبط جيدًا',
      'وحيدة', 'مدعومة', 'ضباب ذهني', 'حاضرة',
    ],
  },

  assessment: {
    title: 'الفحص',
    instruments: {
      EPDS: 'مقياس إدنبرة لاكتئاب ما بعد الولادة',
      GAD7: 'فحص القلق (GAD-7)',
      PHQ9: 'فحص الاكتئاب (PHQ-9)',
    },
    intro: {
      description: 'هذه أداة فحص معتمدة يستخدمها الأطباء لفهم كيف كنتِ تشعرين خلال الأيام السبعة الماضية. لا توجد إجابات صحيحة أو خاطئة.',
      questions:   '{{n}} أسئلة',
      time:        'يستغرق نحو 5 دقائق',
      shared:      'النتائج تُشارك مع فريق رعايتكِ',
      begin:       'ابدئي الفحص',
      notNow:      'ليس الآن',
    },
    inPast7Days: 'خلال الأيام السبعة الماضية…',
    crisisNotice:'إجابتكِ على هذا السؤال تساعدنا في التأكد من حصولكِ على الدعم المناسب.',
    submitting:  'جارٍ مراجعة إجاباتكِ…',
    result: {
      score:           'النتيجة',
      whatHappensNext: 'ما الخطوة التالية',
      backHome:        'العودة إلى الرئيسية',
      talkCompanion:   'تحدّثي مع مرافقكِ',
      crisis: {
        title:       'أنتِ لستِ وحدكِ — المساعدة متاحة الآن',
        teamAlerted: 'تم إبلاغ فريق رعايتكِ وسيتواصلون معكِ قريبًا.',
      },
    },
  },

  companion: {
    welcome: 'مرحبًا، أنا هنا معكِ. هذه مساحة أمان لمشاركة ما تشعرين به — أي شيء يدور في ذهنكِ. كيف حالكِ اليوم؟',
    name:        'مرافقكِ',
    subtitle:    'سري · مستند إلى الرعاية السريرية · متاح دائمًا',
    needHelp:    'تحتاجين مساعدة الآن؟',
    placeholder: 'شاركي ما يشغل تفكيركِ…',
    disclaimer:  'هذا لا يُغني عن الرعاية المهنية.',
    immediateHelp: 'تحتاجين مساعدة فورية؟',
    starters: [
      'كنت أشعر بالإرهاق الشديد مؤخرًا',
      'أعاني من صعوبة في النوم وهذا يؤثر على مزاجي',
      'لا أشعر بالارتباط بطفلتي كما كنت أتوقع',
      'أشعر بالقلق من فكرة أن أصبح أمًا',
      'لست متأكدة مما إذا كان ما أشعر به طبيعيًا',
    ],
    crisis: {
      title:   'دعم فوري',
      message: 'أنتِ لستِ وحدكِ. هذه الموارد مجانية ومتاحة الآن.',
    },
  },

  content: {
    title:    'التعلّم',
    subtitle: 'موارد مبنية على الأدلة لرحلتكِ',
    search:   'ابحثي في المقالات والتمارين…',
    filters: {
      all:      'الكل',
      article:  'مقال',
      exercise: 'تمرين',
      audio:    'صوت',
      module:   'وحدة',
    },
    categories: ['القلق', 'النوم', 'الترابط', 'العناية بالذات', 'العلاقات', 'الحزن'],
    empty:     'لا توجد محتويات. جرّبي بحثًا مختلفًا.',
    minRead:   'قراءة {{n}} دقيقة',
    minDuration:'{{n}} دقيقة',
  },

  care: {
    title:          'خطة الرعاية',
    noplan:         'لا توجد خطة رعاية بعد',
    noplanSub:      'أكملي أول تقييم وسيقوم مدير الرعاية بإنشاء خطة مخصصة لكِ خلال 48 ساعة.',
    startAssessment:'ابدئي بأول فحص',
    goals:          'أهدافكِ',
    todaysTasks:    'مهام اليوم',
    complete:       'وضّعي علامة مكتمل',
    completed:      'مكتمل',
    nextReview:     'المراجعة القادمة مع فريق رعايتكِ:',
    categories: {
      self_care:  'العناية بالذات',
      therapy:    'العلاج',
      medication: 'الدواء',
      social:     'التواصل الاجتماعي',
      exercise:   'الرياضة',
      sleep:      'النوم',
      nutrition:  'التغذية',
      other:      'أخرى',
    },
  },

  profile: {
    deleteAccount: {
      title: "حذف الحساب",
      sub: "إزالة حسابكِ وكل ما فيه",
      confirmTitle: "هل تريدين حذف حسابكِ؟",
      confirmBody: "سيُفرَّغ هذا الهاتف فورًا — فلا يجد فيه شيئًا من يتناوله. وتُحفَظ بياناتكِ لمدة 30 يومًا إن غيّرتِ رأيكِ؛ يمكنكِ تسجيل الدخول ببريدكِ وكلمة المرور لاستعادتها. وبعد ذلك تُتلَف نهائيًا.",
      retainNote: "نحتفظ بشيئين ونفضّل التصريح بهما: سجلّ يفيد بوجود حساب وحذفه، دون أي تفاصيل شخصية، وأي حالة أزمة جرى تصعيدها إلى مختصّ سريري.",
      cancel: "الاحتفاظ بحسابي",
      confirm: "حذف حسابي",
      working: "جارٍ الحذف…",
      failed: "تعذّر حذف حسابكِ. يُرجى المحاولة مجددًا أو مراسلتنا على support@maternally.care.",
    },
    completeSetup: 'أكملي إعداد حسابكِ',
    completeSetupSub: 'أضيفي اسمكِ ومرحلتكِ متى ما كنتِ مستعدة',
    notSetYet: 'لم تُحدَّد بعد',
    title:             'الملف الشخصي',
    myJourney:         'رحلتي',
    stage:             'المرحلة',
    epdsScreening:     'فحص EPDS',
    takeNow:           'أجري الفحص الآن ←',
    carePlan:          'خطة الرعاية',
    view:              'عرض ←',
    notifications:     'الإشعارات',
    dailyReminders:    'تذكيرات المزاج اليومية',
    dailyRemindersSub: 'تذكير لطيف كل صباح',
    smsAlerts:         'تنبيهات الرسائل النصية',
    smsAlertsSub:      'رسائل فريق الرعاية عبر النص',
    privacy:           'الخصوصية والبيانات',
    research:          'المساهمة في البحث',
    researchSub:       'بيانات مجهولة فقط — تساعد في تحسين الرعاية لجميع الأمهات',
    encryption:        'تشفير البيانات',
    compliance:        'الامتثال',
    support:           'الدعم',
    crisisResources:   'موارد الأزمات',
    appVersion:        'إصدار التطبيق',
    signOut:           'تسجيل الخروج',
    stageName: {
      trying:     'أحاول الحمل',
      pregnant:   'حامل',
      postpartum: 'ما بعد الولادة',
      loss:       'فقدان الحمل',
    },
  },

  crisis: {
    title:     'أنتِ لستِ وحدكِ',
    subtitle:  'المساعدة متاحة الآن — مجانية وسرية وعلى مسافة مكالمة أو رسالة.',
    fact:      'اضطرابات المزاج والقلق حول الولادة هي المضاعفة الأكثر شيوعًا للحمل وفترة ما بعد الولادة — تؤثر على 1 من كل 5 أمهات. مع الدعم الصحيح، التعافي ممكن وشائع جدًا.',
    factTitle: 'أنتِ مهمة — وكذلك الحصول على الدعم',
    backToApp: '← العودة إلى متيرنلي',
  },

  languageSwitcher: {
    label:   'اللغة',
    english: 'English',
    arabic:  'العربية',
    french:  'Français',
  },
};

export default ar;
